import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import type { DocumentChain } from "@/lib/dokumente";
import { openElectronInOtherWindow, otherWindowToastMessage } from "@/lib/electron";
import { toast } from "@/lib/toast";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { DocumentChainDialog } from "@/pages/dokumente/DocumentChainDialog";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

async function openInOtherWindowOrFallback(
  path: string,
  fallback: () => void,
): Promise<void> {
  const target = await openElectronInOtherWindow(path);
  if (target) {
    toast.info(otherWindowToastMessage(target));
    return;
  }
  fallback();
}

interface InvoiceDraftContextModalsValue {
  openProjectOverview: (auftragId: number) => void;
  openTimesheet: (timesheetId: number) => void;
  openChain: () => void;
  closeChain: () => void;
  /** Schließt alle In-App-Kontextmodale (Projekt, Stundennachweis, Kette). */
  closeInAppContextModals: () => void;
}

const InvoiceDraftContextModalsContext =
  createContext<InvoiceDraftContextModalsValue | null>(null);

export function useInvoiceDraftContextModals(): InvoiceDraftContextModalsValue {
  const ctx = useContext(InvoiceDraftContextModalsContext);
  if (!ctx) {
    throw new Error(
      "useInvoiceDraftContextModals must be used within InvoiceDraftContextModalsProvider",
    );
  }
  return ctx;
}

/** Optional – Buttons außerhalb des Providers bleiben nutzbar mit Fallback. */
export function useOptionalInvoiceDraftContextModals(): InvoiceDraftContextModalsValue | null {
  return useContext(InvoiceDraftContextModalsContext);
}

export function InvoiceDraftContextModalsProvider({
  chain,
  docId,
  children,
}: {
  chain: DocumentChain | null;
  docId: number | null;
  children: ReactNode;
}) {
  const { open: openProject, close: closeProject } = useProjectOverview();
  const { openDetail, close: closeTimesheet } = useTimesheetFlow();
  const { changeDocId } = useDocumentEditor();
  const [chainOpen, setChainOpen] = useState(false);

  const closeInAppContextModals = useCallback(() => {
    closeProject();
    closeTimesheet();
    setChainOpen(false);
  }, [closeProject, closeTimesheet]);

  const openProjectOverview = useCallback(
    (auftragId: number) => {
      // In-App-Kontextmodale schließen, dann Zweitfenster oder In-App-Modal.
      closeInAppContextModals();
      void openInOtherWindowOrFallback(`/projekte?auftrag_id=${auftragId}`, () => {
        openProject(auftragId);
      });
    },
    [closeInAppContextModals, openProject],
  );

  const openTimesheet = useCallback(
    (timesheetId: number) => {
      closeInAppContextModals();
      void openInOtherWindowOrFallback(
        `/dokumente/stundennachweise?timesheet_id=${timesheetId}`,
        () => openDetail(timesheetId),
      );
    },
    [closeInAppContextModals, openDetail],
  );

  const openChain = useCallback(() => {
    const id = Number(docId);
    if (!Number.isFinite(id) || id <= 0) return;
    closeInAppContextModals();
    // `?kette=` öffnet DocumentChainDeepLinkBridge im Zielfenster (nicht den Editor).
    void openInOtherWindowOrFallback(`/dokumente?kette=${id}`, () => {
      setChainOpen(true);
    });
  }, [closeInAppContextModals, docId]);

  const closeChain = useCallback(() => setChainOpen(false), []);

  const value = useMemo(
    () => ({
      openProjectOverview,
      openTimesheet,
      openChain,
      closeChain,
      closeInAppContextModals,
    }),
    [openProjectOverview, openTimesheet, openChain, closeChain, closeInAppContextModals],
  );

  return (
    <InvoiceDraftContextModalsContext.Provider value={value}>
      {children}
      <DocumentChainDialog
        open={chainOpen}
        onClose={closeChain}
        chain={chain}
        docId={docId}
        currentDocId={docId}
        onOpenDocument={(nextId) => {
          setChainOpen(false);
          changeDocId(nextId);
        }}
      />
    </InvoiceDraftContextModalsContext.Provider>
  );
}
